import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Chart, registerables } from 'chart.js';
import { MatrixController, MatrixElement } from 'chartjs-chart-matrix';
import { combineLatest, Observable } from 'rxjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { filter, map } from 'rxjs/operators';
import { dateDiff, toNaiveISO } from '../../../helper/datehelper';
import { ReportingService } from '../../../store/reporting/reporting.service';
import {
  GroupedLabelWithValue as GroupedLabelWithValue,
  InputReporting,
} from '../../../store/reporting/types';
import { TimeZoneService } from '../../../../common/time-zone/time-zone.service';
import { debugLog } from '../../../pipe/rxjs/operators';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() startDate: Date;
  @Input() endDate: Date;
  @Input() salesTeamId: number;
  @Input() salesTeamMemberId: number;

  @Input() productCategoryIds: number[] = [];
  @Input() salesTeamIds: number[] = [];
  @Input() salesTeamMemberUserIds: number[] = [];
  @Input() campaignIds: number[] = [];
  @Input() sourceIds: number[] = [];
  @Input() mediumIds: number[] = [];

  //"All" Value
  @Input() productCategoryIdAllValue: number = -1; //id
  @Input() salesTeamIdAllValue: number = -1; //id
  @Input() salesTeamMemberUserIdAllValue: number = -1; //userId
  @Input() campaignIdAllValue: number = -1; //id
  @Input() sourceIdAllValue: number = -1; //id
  @Input() mediumIdAllValue: number = -1; //id

  //Observable KPI
  leadCount$: Observable<number>;
  conversionRate$: Observable<number>;
  rawContractCount$: Observable<number>;
  validatedContractCount$: Observable<number>;

  //Observable Charts
  leadsBySource$: Observable<GroupedLabelWithValue[]>;
  contractsBySource$: Observable<GroupedLabelWithValue[]>;
  leadByProductCategory$: Observable<GroupedLabelWithValue[]>;
  contractsByProductCategory$: Observable<GroupedLabelWithValue[]>;
  leadBySalesPerson$: Observable<GroupedLabelWithValue[]>;
  contractsBySalesPerson$: Observable<GroupedLabelWithValue[]>;

  //Chart
  private leadByProductCategoryChart: any;
  private contractsByProductCategoryChart: any;
  private leadBySourceChart: any;
  private contractsBySourceChart: any;
  private leadBySalesPersonChart: any;
  private contractsBySalesPersonChart: any;

  private filterPeriod: string;
  private numberOfDays: any;

  private fontFamily: string;

  //const
  statusFilter = ',StatusId>1';

  constructor(
    private readonly reportingService: ReportingService,
    private readonly timeZoneService: TimeZoneService,
  ) {}

  ngOnInit(): void {
    //Bar Chart
    //Matrix (Heat) Chart
    Chart.register(...registerables);
    Chart.register(MatrixController, MatrixElement);

    this.numberOfDays = dateDiff(this.startDate, this.endDate);

    const endDatePlusOne: Date = new Date(this.endDate);
    endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);

    this.filterPeriod = `CreationDate>=${this.timeZoneService.fromLocalToUtc(
      toNaiveISO(this.startDate),
      false,
    )},CreationDate<${this.timeZoneService.fromLocalToUtc(
      toNaiveISO(endDatePlusOne),
      false,
    )}`;

    let filterString: string = this.filterPeriod;
    if (
      this.productCategoryIds.length != 0 &&
      !this.productCategoryIds.includes(this.productCategoryIdAllValue)
    ) {
      filterString = filterString + ',(';
      let productCategoryFilters: string[] = [];
      for (let i = 0; i < this.productCategoryIds.length; i++) {
        productCategoryFilters.push(
          `ProductCategoryId=${this.productCategoryIds[i]}`,
        );
      }
      filterString = filterString + productCategoryFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.salesTeamIds.length != 0 &&
      !this.salesTeamIds.includes(this.salesTeamIdAllValue)
    ) {
      filterString = filterString + ',(';

      let salesTeamFilters: string[] = [];
      for (let i = 0; i < this.salesTeamIds.length; i++) {
        switch (this.salesTeamIds[i]) {
          case -2:
            salesTeamFilters.push('SalesTeamId=null');
            break;
          default:
            salesTeamFilters.push(`SalesTeamId=${this.salesTeamIds[i]}`);
            break;
        }
      }
      filterString = filterString + salesTeamFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.salesTeamMemberUserIds.length != 0 &&
      !this.salesTeamMemberUserIds.includes(this.salesTeamMemberUserIdAllValue)
    ) {
      filterString = filterString + ',(';

      let salesTeamMemberUserFilters: string[] = [];
      for (let i = 0; i < this.salesTeamMemberUserIds.length; i++) {
        switch (this.salesTeamMemberUserIds[i]) {
          case -2:
            salesTeamMemberUserFilters.push('SalesPersonId=null');
            break;
          default:
            salesTeamMemberUserFilters.push(
              `SalesPersonId=${this.salesTeamMemberUserIds[i]}`,
            );
            break;
        }
      }
      filterString = filterString + salesTeamMemberUserFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.campaignIds.length != 0 &&
      !this.campaignIds.includes(this.campaignIdAllValue)
    ) {
      filterString = filterString + ',(';

      let campaignFilters: string[] = [];
      for (let i = 0; i < this.campaignIds.length; i++) {
        switch (this.campaignIds[i]) {
          case -2:
            campaignFilters.push('CampaignId=null');
            break;
          default:
            campaignFilters.push(`CampaignId=${this.campaignIds[i]}`);
            break;
        }
      }
      filterString = filterString + campaignFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.sourceIds.length != 0 &&
      !this.sourceIds.includes(this.sourceIdAllValue)
    ) {
      filterString = filterString + ',(';

      let sourceFilters: string[] = [];
      for (let i = 0; i < this.sourceIds.length; i++) {
        switch (this.sourceIds[i]) {
          case -2:
            sourceFilters.push(`SourceId=null`);
            break;
          default:
            sourceFilters.push(`SourceId=${this.sourceIds[i]}`);
            break;
        }
      }
      filterString = filterString + sourceFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.mediumIds.length != 0 &&
      !this.mediumIds.includes(this.mediumIdAllValue)
    ) {
      filterString = filterString + ',(';

      let mediumFilters: string[] = [];
      for (let i = 0; i < this.mediumIds.length; i++) {
        switch (this.mediumIds[i]) {
          case -2:
            mediumFilters.push(`MediumId=null`);
            break;
          default:
            mediumFilters.push(`MediumId=${this.mediumIds[i]}`);
            break;
        }
      }
      filterString = filterString + mediumFilters.join('|');
      filterString = filterString + ')';
    }

    this.leadCount$ = this.reportingService.getLeadCount.value$;
    this.rawContractCount$ = this.reportingService.getRawContractCount.value$;
    this.validatedContractCount$ =
      this.reportingService.getValidatedContractCount.value$;
    this.conversionRate$ = combineLatest([
      this.rawContractCount$,
      this.leadCount$,
    ]).pipe(
      map(([rawContractsCount, leadsCount]) => {
        if (!leadsCount || leadsCount === 0) {
          return 0;
        }
        return (rawContractsCount / leadsCount) * 100;
      }),
    );

    //init Observable
    this.leadByProductCategory$ =
      this.reportingService.getReportLeadsByCategory.value$;
    this.contractsByProductCategory$ =
      this.reportingService.getReportContractsByProductCategory.value$;
    this.leadsBySource$ = this.reportingService.getReportLeadsBySource.value$;
    this.contractsBySource$ =
      this.reportingService.getReportContractsBySource.value$;
    this.leadBySalesPerson$ =
      this.reportingService.getReportLeadsBySalesPerson.value$;
    this.contractsBySalesPerson$ =
      this.reportingService.getReportContractsBySalesPerson.value$;

    this.fontFamily = 'Roboto';

    this.kpiCallApi(filterString);

    this.chartsCallApi(filterString);

    this.leadsPerCategoryChartSetup(this.fontFamily);

    this.contractsPerCategoryChartSetup(this.fontFamily);

    this.leadsPerSalesPersonChartSetup(this.fontFamily);

    this.contractsPerSalesPersonChartSetup(this.fontFamily);

    this.leadsPerSourceChartSetup(this.fontFamily);

    this.contractsPerSourceChartSetup(this.fontFamily);
  }

  private chartsCallApi(filterString: string) {
    //Get leads by product category
    let leadByProductCategoryQueryStrings: Map<string, string> = new Map<
      string,
      string
    >();
    leadByProductCategoryQueryStrings.set('entity', 'Lead');
    leadByProductCategoryQueryStrings.set('filter', filterString);
    leadByProductCategoryQueryStrings.set(
      'startDate',
      new Date(this.startDate).toDateString(),
    );
    leadByProductCategoryQueryStrings.set(
      'endDate',
      new Date(this.endDate).toDateString(),
    );
    leadByProductCategoryQueryStrings.set('groupedProperty', 'ProductCategory');

    let inputReportLeadsByCategory: InputReporting = {
      parameters: ['groupedRange'],
      query: leadByProductCategoryQueryStrings,
    };

    //call
    this.reportingService.getReportLeadsByCategory.call(
      inputReportLeadsByCategory,
    );

    //Get contracts by product category
    let contractByProductCategoryQueryStrings: Map<string, string> = new Map<
      string,
      string
    >();
    contractByProductCategoryQueryStrings.set('entity', 'Contract');
    contractByProductCategoryQueryStrings.set(
      'filter',
      filterString + this.statusFilter,
    );
    contractByProductCategoryQueryStrings.set(
      'startDate',
      new Date(this.startDate).toDateString(),
    );
    contractByProductCategoryQueryStrings.set(
      'endDate',
      new Date(this.endDate).toDateString(),
    );
    contractByProductCategoryQueryStrings.set(
      'groupedProperty',
      'ProductCategory',
    );

    let inputReportContractsByCategory: InputReporting = {
      parameters: ['groupedRange'],
      query: contractByProductCategoryQueryStrings,
    };

    //call
    this.reportingService.getReportContractsByProductCategory.call(
      inputReportContractsByCategory,
    );

    //Get leads by source
    let leadBySourceQueryStrings: Map<string, string> = new Map<
      string,
      string
    >();
    leadBySourceQueryStrings.set('entity', 'Lead');
    leadBySourceQueryStrings.set('filter', filterString);
    leadBySourceQueryStrings.set(
      'startDate',
      new Date(this.startDate).toDateString(),
    );
    leadBySourceQueryStrings.set(
      'endDate',
      new Date(this.endDate).toDateString(),
    );
    leadBySourceQueryStrings.set('groupedProperty', 'Source');

    let inputReportLeadsBySource: InputReporting = {
      parameters: ['groupedRange'],
      query: leadBySourceQueryStrings,
    };

    //call
    this.reportingService.getReportLeadsBySource.call(inputReportLeadsBySource);

    //Get contracts by source
    let contractBySourceQueryStrings: Map<string, string> = new Map<
      string,
      string
    >();
    contractBySourceQueryStrings.set('entity', 'Contract');
    contractBySourceQueryStrings.set(
      'filter',
      filterString + this.statusFilter,
    );
    contractBySourceQueryStrings.set(
      'startDate',
      new Date(this.startDate).toDateString(),
    );
    contractBySourceQueryStrings.set(
      'endDate',
      new Date(this.endDate).toDateString(),
    );
    contractBySourceQueryStrings.set('groupedProperty', 'Source');

    let inputReportContractsBySource: InputReporting = {
      parameters: ['groupedRange'],
      query: contractBySourceQueryStrings,
    };

    //call
    this.reportingService.getReportContractsBySource.call(
      inputReportContractsBySource,
    );

    //Get leads by sales person
    let leadBySalesPersonQueryStrings: Map<string, string> = new Map<
      string,
      string
    >();
    leadBySalesPersonQueryStrings.set('entity', 'Lead');
    leadBySalesPersonQueryStrings.set('filter', filterString);
    leadBySalesPersonQueryStrings.set(
      'startDate',
      new Date(this.startDate).toDateString(),
    );
    leadBySalesPersonQueryStrings.set(
      'endDate',
      new Date(this.endDate).toDateString(),
    );
    leadBySalesPersonQueryStrings.set('groupedProperty', 'SalesPerson');

    let inputReportLeadsBySalesPerson: InputReporting = {
      parameters: ['groupedRange'],
      query: leadBySalesPersonQueryStrings,
    };

    //call
    this.reportingService.getReportLeadsBySalesPerson.call(
      inputReportLeadsBySalesPerson,
    );

    //Get contracts by sales person
    let contractBySalesPersonQueryStrings: Map<string, string> = new Map<
      string,
      string
    >();
    contractBySalesPersonQueryStrings.set('entity', 'Contract');
    contractBySalesPersonQueryStrings.set(
      'filter',
      filterString + this.statusFilter,
    );
    contractBySalesPersonQueryStrings.set(
      'startDate',
      new Date(this.startDate).toDateString(),
    );
    contractBySalesPersonQueryStrings.set(
      'endDate',
      new Date(this.endDate).toDateString(),
    );
    contractBySalesPersonQueryStrings.set('groupedProperty', 'SalesPerson');

    let inputReportContractsBySalesPerson: InputReporting = {
      parameters: ['groupedRange'],
      query: contractBySalesPersonQueryStrings,
    };

    //call
    this.reportingService.getReportContractsBySalesPerson.call(
      inputReportContractsBySalesPerson,
    );
  }

  private kpiCallApi(filterString: string) {
    let inputLeadsCount: InputReporting = {
      parameters: ['count'],
      query: new Map([
        ['filter', filterString],
        ['entity', 'Lead'],
      ]),
    };
    this.reportingService.getLeadCount.call(inputLeadsCount);

    let rawContractFilterString = filterString;
    if (rawContractFilterString) {
      rawContractFilterString = rawContractFilterString + ',StatusId>1';
    }
    let inputRawContractCount: InputReporting = {
      parameters: ['count'],
      query: new Map([
        ['filter', rawContractFilterString],
        ['entity', 'Contract'],
      ]),
    };
    this.reportingService.getRawContractCount.call(inputRawContractCount);

    let validatedContractFilterString = filterString;
    if (validatedContractFilterString) {
      validatedContractFilterString =
        validatedContractFilterString + ',StatusId=5';
    }
    let inputValidatedContractCount: InputReporting = {
      parameters: ['count'],
      query: new Map([
        ['filter', validatedContractFilterString],
        ['entity', 'Contract'],
      ]),
    };
    this.reportingService.getValidatedContractCount.call(
      inputValidatedContractCount,
    );
  }

  ngOnDestroy(): void {
    this.reportingService.getLeadCount.reset();
    this.reportingService.getRawContractCount.reset();
    this.reportingService.getValidatedContractCount.reset();
    this.reportingService.getReportLeadsByCategory.reset();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.numberOfDays = dateDiff(this.startDate, this.endDate);

    const endDatePlusOne: Date = new Date(this.endDate);
    endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);

    this.filterPeriod = `CreationDate>=${this.timeZoneService.fromLocalToUtc(
      toNaiveISO(this.startDate),
      false,
    )},CreationDate<${this.timeZoneService.fromLocalToUtc(
      toNaiveISO(endDatePlusOne),
      false,
    )}`;

    //filter input
    let filterString: string = this.filterPeriod;

    if (
      this.productCategoryIds.length != 0 &&
      !this.productCategoryIds.includes(this.productCategoryIdAllValue)
    ) {
      filterString = filterString + ',(';
      let productCategoryFilters: string[] = [];
      for (let i = 0; i < this.productCategoryIds.length; i++) {
        productCategoryFilters.push(
          `ProductCategoryId=${this.productCategoryIds[i]}`,
        );
      }
      filterString = filterString + productCategoryFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.salesTeamIds.length != 0 &&
      !this.salesTeamIds.includes(this.salesTeamIdAllValue)
    ) {
      filterString = filterString + ',(';

      let salesTeamFilters: string[] = [];
      for (let i = 0; i < this.salesTeamIds.length; i++) {
        switch (this.salesTeamIds[i]) {
          case -2:
            salesTeamFilters.push('SalesTeamId=null');
            break;
          default:
            salesTeamFilters.push(`SalesTeamId=${this.salesTeamIds[i]}`);
            break;
        }
      }
      filterString = filterString + salesTeamFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.salesTeamMemberUserIds.length != 0 &&
      !this.salesTeamMemberUserIds.includes(this.salesTeamMemberUserIdAllValue)
    ) {
      filterString = filterString + ',(';

      let salesTeamMemberUserFilters: string[] = [];
      for (let i = 0; i < this.salesTeamMemberUserIds.length; i++) {
        switch (this.salesTeamMemberUserIds[i]) {
          case -2:
            salesTeamMemberUserFilters.push('SalesPersonId=null');
            break;
          default:
            salesTeamMemberUserFilters.push(
              `SalesPersonId=${this.salesTeamMemberUserIds[i]}`,
            );
            break;
        }
      }
      filterString = filterString + salesTeamMemberUserFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.campaignIds.length != 0 &&
      !this.campaignIds.includes(this.campaignIdAllValue)
    ) {
      filterString = filterString + ',(';

      let campaignFilters: string[] = [];
      for (let i = 0; i < this.campaignIds.length; i++) {
        switch (this.campaignIds[i]) {
          case -2:
            campaignFilters.push('CampaignId=null');
            break;
          default:
            campaignFilters.push(`CampaignId=${this.campaignIds[i]}`);
            break;
        }
      }
      filterString = filterString + campaignFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.sourceIds.length != 0 &&
      !this.sourceIds.includes(this.sourceIdAllValue)
    ) {
      filterString = filterString + ',(';

      let sourceFilters: string[] = [];
      for (let i = 0; i < this.sourceIds.length; i++) {
        switch (this.sourceIds[i]) {
          case -2:
            sourceFilters.push('SourceId=null');
            break;
          default:
            sourceFilters.push(`SourceId=${this.sourceIds[i]}`);
            break;
        }
      }
      filterString = filterString + sourceFilters.join('|');
      filterString = filterString + ')';
    }
    if (
      this.mediumIds.length != 0 &&
      !this.mediumIds.includes(this.mediumIdAllValue)
    ) {
      filterString = filterString + ',(';

      let mediumFilters: string[] = [];
      for (let i = 0; i < this.mediumIds.length; i++) {
        switch (this.mediumIds[i]) {
          case -2:
            mediumFilters.push('MediumId=null');
            break;
          default:
            mediumFilters.push(`MediumId=${this.mediumIds[i]}`);
            break;
        }
      }
      filterString = filterString + mediumFilters.join('|');
      filterString = filterString + ')';
    }
    // console.log('logfilterchanges', filterString);
    this.kpiCallApi(filterString);
    this.chartsCallApi(filterString);
  }

  private leadsPerCategoryChartSetup(fontFamily: string) {
    this.leadByProductCategory$.pipe(filter((x) => !!x)).subscribe((result) => {
      const chartData = this.buildStackedBarChartData(result);
      const maxStack = this.getMaxStack(chartData);
      if (this.leadByProductCategoryChart) {
        this.leadByProductCategoryChart.clear();
        this.leadByProductCategoryChart.data = chartData;
        this.leadByProductCategoryChart.options = getStackedBarChartConfig(
          chartData,
          fontFamily,
          maxStack,
        ).options;
        this.leadByProductCategoryChart.update();
      } else {
        this.leadByProductCategoryChart = new Chart(
          'leadByProductCategoryChart',
          getStackedBarChartConfig(chartData, fontFamily, maxStack),
        );
      }
    });
  }

  private contractsPerCategoryChartSetup(fontFamily: string) {
    this.contractsByProductCategory$
      .pipe(filter((x) => !!x))
      .subscribe((result) => {
        const chartData = this.buildStackedBarChartData(result);
        const maxStack = this.getMaxStack(chartData);
        if (this.contractsByProductCategoryChart) {
          this.contractsByProductCategoryChart.clear();
          this.contractsByProductCategoryChart.data = chartData;
          this.contractsByProductCategoryChart.options =
            getStackedBarChartConfig(chartData, fontFamily, maxStack).options;
          this.contractsByProductCategoryChart.update();
        } else {
          this.contractsByProductCategoryChart = new Chart(
            'contractsByProductCategoryChart',
            getStackedBarChartConfig(chartData, fontFamily, maxStack),
          );
        }
      });
  }

  private leadsPerSourceChartSetup(fontFamily: string) {
    this.leadsBySource$.pipe(filter((x) => !!x)).subscribe((result) => {
      const chartData = this.buildStackedBarChartData(result);
      const maxStack = this.getMaxStack(chartData);
      if (this.leadBySourceChart) {
        this.leadBySourceChart.clear();
        this.leadBySourceChart.data = chartData;
        this.leadBySourceChart.options = getStackedBarChartConfig(
          chartData,
          fontFamily,
          maxStack,
        ).options;
        this.leadBySourceChart.update();
      } else {
        this.leadBySourceChart = new Chart(
          'leadBySourceChart',
          getStackedBarChartConfig(chartData, fontFamily, maxStack),
        );
      }
    });
  }

  private contractsPerSourceChartSetup(fontFamily: string) {
    this.contractsBySource$.pipe(filter((x) => !!x)).subscribe((result) => {
      const chartData = this.buildStackedBarChartData(result);
      const maxStack = this.getMaxStack(chartData);
      if (this.contractsBySourceChart) {
        this.contractsBySourceChart.clear();
        this.contractsBySourceChart.data = chartData;
        this.contractsBySourceChart.options = getStackedBarChartConfig(
          chartData,
          fontFamily,
          maxStack,
        ).options;
        this.contractsBySourceChart.update();
      } else {
        this.contractsBySourceChart = new Chart(
          'contractsBySourceChart',
          getStackedBarChartConfig(chartData, fontFamily, maxStack),
        );
      }
    });
  }

  private leadsPerSalesPersonChartSetup(fontFamily: string) {
    this.leadBySalesPerson$.pipe(filter((x) => !!x)).subscribe((result) => {
      const chartData = this.buildStackedBarChartData(result);
      const maxStack = this.getMaxStack(chartData);
      if (this.leadBySalesPersonChart) {
        this.leadBySalesPersonChart.clear();
        this.leadBySalesPersonChart.data = chartData;
        this.leadBySalesPersonChart.options = getStackedBarChartConfig(
          chartData,
          fontFamily,
          maxStack,
        ).options;
        this.leadBySalesPersonChart.update();
      } else {
        this.leadBySalesPersonChart = new Chart(
          'leadBySalesPersonChart',
          getStackedBarChartConfig(chartData, fontFamily, maxStack),
        );
      }
    });
  }

  private contractsPerSalesPersonChartSetup(fontFamily: string) {
    this.contractsBySalesPerson$
      .pipe(filter((x) => !!x))
      .subscribe((result) => {
        const chartData = this.buildStackedBarChartData(result);
        const maxStack = this.getMaxStack(chartData);
        if (this.contractsBySalesPersonChart) {
          this.contractsBySalesPersonChart.clear();
          this.contractsBySalesPersonChart.data = chartData;
          this.contractsBySalesPersonChart.options = getStackedBarChartConfig(
            chartData,
            fontFamily,
            maxStack,
          ).options;
          this.contractsBySalesPersonChart.update();
        } else {
          this.contractsBySalesPersonChart = new Chart(
            'contractsBySalesPersonChart',
            getStackedBarChartConfig(chartData, fontFamily, maxStack),
          );
        }
      });
  }
  // Helper to build chart data for stacked bar charts
  private buildStackedBarChartData(result: any[]): stackedBarChartData {
    const labels = result[0].values.map((dataset: any) => dataset.label);
    const datasets = result.map((element, index) => ({
      label: element.groupLabel,
      data: element.values.map((v: any) => v.value),
      backgroundColor: this.getStackedBarChartColor(index),
    }));
    return { labels, datasets };
  }

  // Helper to get max stack value for y-axis
  private getMaxStack(data: stackedBarChartData): number {
    return data.labels.reduce((max, _label, idx) => {
      const stackSum = data.datasets.reduce(
        (sum, ds) => sum + (ds.data[idx] || 0),
        0,
      );
      return Math.max(max, stackSum);
    }, 0);
  }

  private getStackedBarChartColor(index: number): string {
    const colorVars = [
      '--charts-color-primary',
      '--charts-color-orange',
      '--charts-color-green',
      '--charts-color-purple',
      '--charts-color-yellow',
      '--charts-color-red',
      '--charts-color-teal',
      '--charts-color-pink',
    ];
    const style = window.getComputedStyle(document.body);
    return style.getPropertyValue(colorVars[index % colorVars.length]);
  }
}

export type stackedBarChartData = {
  labels: string[];
  datasets: dataSet[];
};

export type dataSet = {
  label: string;
  data: any[];
  backgroundColor: string;
};

// Centralized Chart.js config for stacked bar charts
export function getStackedBarChartConfig(
  data: stackedBarChartData,
  fontFamily: string,
  maxStack: number,
): any {
  return {
    type: 'bar',
    data,
    plugins: [ChartDataLabels],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        datalabels: {
          display: true,
          anchor: 'end',
          align: 'end',
          color: '#000',
          font: {
            weight: 'bold',
            family: fontFamily,
          },
          formatter: datalabelsFormatter,
        },
        legend: {
          display: true,
          position: 'bottom',
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          filter: tooltipFilter,
          callbacks: {
            label: tooltipLabel,
          },
        },
      },
      scales: {
        x: {
          stacked: true,
        },
        y: {
          beginAtZero: true,
          stacked: true,
          //set max value + 2 to avoid cutting off sum label
          suggestedMax: Math.ceil(maxStack + maxStack * 0.25),
        },
      },
      font: {
        family: fontFamily,
      },
    },
  };
}

export function datalabelsFormatter(value: any, context: any) {
  const datasets = context.chart.data.datasets;
  const stackTotal = datasets.reduce(
    (total: number, dataset: any) =>
      total + (dataset.data[context.dataIndex] || 0),
    0,
  );
  if (context.datasetIndex === datasets.length - 1 && stackTotal !== 0) {
    return stackTotal;
  }
  return '';
}

export function tooltipFilter(context: any) {
  //don't show the tooltip if all values for an x value are 0
  const chart = context.chart;
  const dataIndex = context.dataIndex;
  const datasets = chart.data.datasets;
  const total = datasets.reduce((sum: number, ds: any) => {
    const v = ds.data[dataIndex];
    return sum + (typeof v === 'number' && !isNaN(v) ? v : 0);
  }, 0);
  return total !== 0;
}

export function tooltipLabel(context: any) {
  const value = context.parsed.y;
  if (value === 0 || value === null || value === undefined) {
    return '';
  }
  return `${context.dataset.label}: ${value}`;
}
